import React from "react";
import _ from "lodash";
import LoadingUI from "./LoadingUI";
import UnitStatus from "./UnitStatus";
import UnitMode from "./UnitMode";
import SegmentModeIndicator from "./SegmentModeIndicator";
import PopupWrapper from "./PopupWrapper";
import SegmentBasicTable from "./SegmentBasicTable";
import ClickToCopyWrapper from "./ClickToCopyWrapper";
import SimpleTooltip from "./SimpleTooltip";
import NumberFormat from "./NumberFormat";
import DateTimeFormatter from "./DateTimeFormatter";
import { UnitAPI } from "apis";
import RealTimeOfDayProgressBar from "./RealTimeOfDayProgressBar";
import CurrencyFormat from "./CurrencyFormat";
import SegmentExperimentModal from "./SegmentExperimentModal";
import UnitHasChildrenButton from "components/snapshot/UnitHasChildrenButton";
import UnitHasParentButton from "components/snapshot/UnitHasParentButton";

class UnitDetailHeader extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      errMsg: null,

      unitInfo: null,

      isLatestExperimentOpen: false,
      segmentId: null,
    };

    this.popup = React.createRef();
  }

  async componentDidMount() {
    this.setState({ isLoading: true });
    try {
      if (!this.props.unitId) {
        throw "Invalid unitId";
      }
      const unitInfo = await UnitAPI.getUnitDetail({
        unitId: this.props.unitId,
      });
      console.log(unitInfo);

      this.props.getUnitInfo(unitInfo);
      this.setState({ unitInfo, isLoading: false, errMsg: null });
    } catch (err) {
      this.setState({
        errMsg: typeof err === "object" ? err.toString() : err,
        isLoading: false,
      });
    }
  }

  render() {
    const { unitInfo, isLoading, errMsg, isLatestExperimentOpen, segmentId } =
      this.state;
    const { isPaused } = this.props;
    // const { unit, config, segments, updatedAt } = this.props;
    const unit = unitInfo;
    const segments = unitInfo && unitInfo.segments;
    const config = unitInfo && unitInfo.autoPilotBrief;

    return (
      // sticky top-0
      <div className="z-50 bg-white shadow-md">
        {isLoading && <LoadingUI iconOnly={true}></LoadingUI>}

        {errMsg && <div className="text-red-800">{errMsg}</div>}

        {unitInfo && (
          <div>
            <div className="border-b px-4 py-2">
              <div className="mt-1 py-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div>
                      <div className="font-semibold text-blue-800">
                        <span className="mr-1 text-blue-700">
                          {unit.networkId}
                        </span>
                        {unit.networkName}
                      </div>
                      <div
                        className="text-xs leading-none text-gray-600"
                        style={{ maxWidth: "200px" }}
                      >
                        YS: {unit.yieldSetId} {unit.yieldSetName}
                      </div>
                    </div>

                    <div className="border-l ml-3 pl-3">
                      <div className="flex">
                        <div className="flex flex-col">
                          <div className="flex text-lg leading-none">
                            <div className="mr-2 font-semibold">
                              {unit.gamUnitId}
                            </div>
                            <div className="font-black">
                              {unit.name}
                              {unit.deletedAt && (
                                <span className="font-normal">
                                  <SimpleTooltip
                                    tooltipText={
                                      <>
                                        Deleted at:{" "}
                                        <DateTimeFormatter
                                          datetime={unit.deletedAt}
                                        ></DateTimeFormatter>
                                      </>
                                    }
                                  >
                                    <span className="rounded mx-1 bg-red-300 px-1 text-xs font-bold uppercase text-red-800">
                                      Deleted
                                    </span>
                                  </SimpleTooltip>
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="mt-1 flex items-center text-xs leading-none text-gray-600">
                            <div
                              className="mr-4 flex items-center"
                              title="Ext Unit ID"
                            >
                              {unit.extUnitId}
                              <ClickToCopyWrapper
                                copyText={unit.extUnitId}
                              ></ClickToCopyWrapper>
                            </div>

                            <div
                              className="mr-4 flex items-center justify-start"
                              title="Ext Unit Path"
                            >
                              <span
                                className="whitespace-no-wrap overflow-x-auto"
                                style={{ maxWidth: "400px" }}
                              >
                                {unit.extUnitPath}
                              </span>
                              <ClickToCopyWrapper
                                copyText={unit.extUnitPath}
                              ></ClickToCopyWrapper>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div>
                      <div className="flex items-center justify-end gap-4 text-sm">
                        {unit.parentUnits && unit.parentUnits.length > 0 && (
                          <div className="pl-1">
                            <UnitHasParentButton
                              unit={unit}
                              isLabelOnly={true}
                            ></UnitHasParentButton>
                          </div>
                        )}
                        {unit.childUnits && unit.childUnits.length > 0 && (
                          <div className="pl-1">
                            <UnitHasChildrenButton
                              unit={unit}
                              isLabelOnly={true}
                            ></UnitHasChildrenButton>
                          </div>
                        )}

                        <div className="flex items-center gap-1 leading-6">
                          <UnitStatus
                            unit={unit}
                            showName={isPaused}
                          ></UnitStatus>
                        </div>

                        <UnitMode unit={unit}></UnitMode>
                      </div>
                    </div>

                    <div className="flex items-end">
                      <AdPodsSection
                        enableAdPods={config.enable_ad_pods}
                      ></AdPodsSection>

                      {unit.explicitlyTargeted > 0 && (
                        <SimpleTooltip
                          tooltipText="Explicitly Targeted"
                          place="bottom"
                        >
                          <div className="rounded mx-2 bg-blue-200 px-1 text-xs font-semibold text-blue-800">
                            EXPL
                          </div>
                        </SimpleTooltip>
                      )}

                      {unit.hasExplicitlyTargetedAncestor > 0 && (
                        <SimpleTooltip
                          tooltipText="Has Explicitly Targeted Ancestor"
                          place="bottom"
                        >
                          <span className="rounded mx-2 bg-blue-200 px-1 text-xs font-semibold text-blue-800">
                            ANC EXPL
                          </span>
                        </SimpleTooltip>
                      )}

                      <InspectionSection
                        unitId={unit.gamUnitId}
                        forceInspection={config.force_inspection}
                      ></InspectionSection>

                      <DebugModeSection
                        enableDispatcherDebug={
                          config.enable_dispatcher_debugging
                        }
                      ></DebugModeSection>

                      <ForerunnerConfigSection
                        value={config.minimum_daily_request_volume}
                      ></ForerunnerConfigSection>

                      <BmSection
                        unitId={unit.gamUnitId}
                        bpm={config.benchmark_performance_multiplier}
                        enableAbuse={config.enable_benchmark_tfcd_abuse}
                        bmAbuseSizes={config.benchmark_abuse_sizes}
                      ></BmSection>

                      <MaxTrafficRatioSection
                        minTrafficRatio={config.minimum_traffic_ratio}
                        maxTrafficRatio={config.maximum_traffic_ratio}
                      ></MaxTrafficRatioSection>

                      <UnitChangeTimeSection
                        unit={unit}
                      ></UnitChangeTimeSection>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-2 flex justify-between">
                <div className="flex gap-4">
                  <div style={{ minWidth: "170px" }}>
                    <span className="block text-xs font-semibold uppercase leading-none text-gray-600">
                      Request Types (
                      {unit.requestTypes ? unit.requestTypes.length : 0})
                    </span>

                    <RequestTypesSection
                      requestTypes={unit.requestTypes}
                    ></RequestTypesSection>
                  </div>

                  <div style={{ minWidth: "170px" }}>
                    <span className="block text-xs font-semibold uppercase leading-none text-gray-600">
                      Demand Types (
                      {unit.demandTypes ? unit.demandTypes.length : 0})
                    </span>
                    <div
                      className="whitespace-no-wrap my-1 mr-1 flex overflow-x-auto"
                      style={{
                        maxWidth: "400px",
                      }}
                    >
                      <DemandTypesSection
                        demandTypes={unit.demandTypes}
                      ></DemandTypesSection>
                    </div>
                  </div>
                </div>

                <div className="text-sm">
                  <span className="block text-xs font-semibold uppercase leading-none text-gray-600">
                    Segments ({segments ? segments.length : 0})
                  </span>
                  {segments && segments.length > 0 && (
                    <PopupWrapper
                      ref={this.popup}
                      place="bottom center"
                      triggerType="hover"
                      hideArrow={true}
                      triggerElement={
                        <div
                          className="flex overflow-x-auto"
                          style={{
                            maxWidth: "400px",
                          }}
                        >
                          <div className="rounded hover:shadow flex bg-gray-300">
                            {segments.map((s) => (
                              <div
                                key={s.segmentId}
                                className="rounded mx-1 flex items-center gap-1 p-1 font-mono"
                              >
                                <SegmentModeIndicator
                                  mode={s.mode}
                                ></SegmentModeIndicator>
                                <span
                                  className={`leading-none ${
                                    s.srConfig
                                      ? "border-b-2 border-purple-400"
                                      : ""
                                  }`}
                                >
                                  {s.segmentId}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      }
                      popupElement={
                        <div style={{ maxWidth: "800px" }}>
                          <SegmentBasicTable
                            segments={segments}
                            latestExperiment={true}
                            openExperimentModal={(segmentId) => {
                              if (this.popup && this.popup.current) {
                                this.popup.current.handleClosePopup();
                              }
                              this.setState({
                                isLatestExperimentOpen: true,
                                segmentId,
                              });
                            }}
                          ></SegmentBasicTable>
                        </div>
                      }
                    ></PopupWrapper>
                  )}
                </div>

                <div className="text-sm">
                  <div className="text-right">
                    CostCPM:{" "}
                    <span
                      style={{ fontSize: "smaller" }}
                      className="text-gray-700"
                    >
                      $
                    </span>
                    {unitInfo.costCPM}{" "}
                    <span
                      style={{ fontSize: "small" }}
                      className="font-mono text-gray-700"
                    >
                      USD
                    </span>
                  </div>

                  {unitInfo.currency !== "USD" && (
                    <div className="text-right text-gray-700">
                      ~{" "}
                      <span
                        style={{ fontSize: "smaller" }}
                        className="text-gray-700"
                      >
                        <CurrencyFormat
                          currency={unitInfo.currency}
                        ></CurrencyFormat>
                      </span>
                      {unitInfo.costCPMInCurrency}{" "}
                      <span
                        style={{ fontSize: "small" }}
                        className="font-mono text-gray-700"
                      >
                        {unitInfo.currency}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end px-4 font-semibold">
              <div className="flex items-center">
                <div className="mr-2">{unit.timezone.replace("_", " ")}</div>
                <div className="text-xs" style={{ marginTop: "2px" }}>
                  <RealTimeOfDayProgressBar
                    timezone={unit.timezone}
                  ></RealTimeOfDayProgressBar>
                </div>
              </div>
              <span className="mx-2 font-hairline text-gray-500">|</span>

              <CurrencyFormat currency={unitInfo.currency}></CurrencyFormat>
              {unit.currency}
            </div>

            <div className="pb-1">
              <div className="flex items-center gap-2">
                <div className="mt-1 inline-block pl-4 text-xs font-semibold uppercase leading-none text-gray-600">
                  Tags ({unit.tags ? unit.tags.length : 0})
                </div>
                <div className="whitespace-no-wrap overflow-scroll">
                  {unit.tags &&
                    unit.tags.map((tag) => {
                      return (
                        <span
                          key={tag}
                          className="rounded mr-2 bg-orange-200 px-2 text-xs text-gray-700"
                        >
                          {tag}
                        </span>
                      );
                    })}
                </div>
              </div>
            </div>

            {isLatestExperimentOpen && (
              <SegmentExperimentModal
                isOpen={isLatestExperimentOpen}
                onClose={() => this.setState({ isLatestExperimentOpen: false })}
                segmentId={segmentId}
                startDate={this.props.startDate}
                segment={_.find(segments, { segmentId: segmentId })}
              ></SegmentExperimentModal>
            )}
          </div>
        )}
      </div>
    );
  }
}

class UnitChangeTimeSection extends React.PureComponent {
  render() {
    const { unit } = this.props;

    return (
      <>
        <SimpleTooltip
          place="left"
          tooltipText={
            <>
              <table>
                <tbody>
                  <tr>
                    <td className="pr-2 text-right">Created At</td>
                    <td>
                      <b>
                        <DateTimeFormatter
                          format="YYYY-MM-DD HH:mm Z"
                          datetime={unit.createdAt}
                        ></DateTimeFormatter>
                      </b>
                    </td>
                  </tr>

                  <tr>
                    <td className="pr-2 text-right">Updated At</td>
                    <td>
                      <b>
                        <DateTimeFormatter
                          format="YYYY-MM-DD HH:mm Z"
                          datetime={unit.updatedAt}
                        ></DateTimeFormatter>
                      </b>
                    </td>
                  </tr>

                  <tr>
                    <td className="pr-2 text-right">
                      Auto Pilot Config Updated At
                    </td>
                    <td>
                      <b>
                        <DateTimeFormatter
                          format="YYYY-MM-DD HH:mm Z"
                          datetime={unit.configUpdatedAt}
                        ></DateTimeFormatter>
                      </b>
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          }
        >
          <div className="whitespace-no-wrap border-l px-1 text-xs text-gray-700">
            Changes made{" "}
            <span className="text-gray-900">
              <DateTimeFormatter
                fromNowOnly={true}
                datetime={unit.lastChangedAt}
              ></DateTimeFormatter>
            </span>
          </div>
        </SimpleTooltip>
      </>
    );
  }
}

class InspectionSection extends React.PureComponent {
  render() {
    const { forceInspection } = this.props;
    if (forceInspection) {
      return (
        <div className="border-l px-1 text-xs" title="Force Inspection">
          {forceInspection && (
            <span className="rounded bg-orange-400 px-2 font-bold text-orange-100">
              INSP
            </span>
          )}
        </div>
      );
    } else {
      return "";
    }
  }
}

class AdPodsSection extends React.PureComponent {
  render() {
    const { enableAdPods } = this.props;

    return (
      <>
        {enableAdPods ? (
          <div className="border-l px-1 text-xs">
            <span className="font-bold text-purple-600">ADPODS</span>
          </div>
        ) : (
          ""
        )}
      </>
    );
  }
}

class DebugModeSection extends React.PureComponent {
  render() {
    const { enableDispatcherDebug } = this.props;
    return enableDispatcherDebug ? (
      <div className="border-l px-1 text-xs">
        <span className="rounded bg-pink-500 px-2 font-bold text-pink-100">
          DEBUG
        </span>
      </div>
    ) : (
      ""
    );
  }
}

class MaxTrafficRatioSection extends React.PureComponent {
  render() {
    const { maxTrafficRatio, minTrafficRatio } = this.props;
    return (
      <div className="border-l px-1 text-xs">
        Traffic:{" "}
        <span className="text-gray-600">
          <NumberFormat value={minTrafficRatio} format="0,0%"></NumberFormat>
        </span>
        <span className="px-1">-</span>
        <span className="text-gray-600">
          <NumberFormat value={maxTrafficRatio} format="0,0%"></NumberFormat>
        </span>
      </div>
    );
  }
}

class ForerunnerConfigSection extends React.PureComponent {
  render() {
    const { value } = this.props;
    return (
      <div className="border-l px-1 text-xs">
        <SimpleTooltip
          place="bottom"
          tooltipText="Forerunner's Minimum Daily Request Volume"
        >
          <span className="text-gray-700">
            Min Req: <NumberFormat value={value} format="0,0"></NumberFormat>
          </span>
        </SimpleTooltip>
      </div>
    );
  }
}

class BmSection extends React.PureComponent {
  render() {
    const { unitId, bpm, enableAbuse, bmAbuseSizes } = this.props;
    /*
    bmAbuseSizes=[
      [
        "fluid",
        [
          1,
          1
        ]
      ]
    ]
    */
    let abuseSizesString = "None";
    if (bmAbuseSizes) {
      let sizes = bmAbuseSizes[0];
      abuseSizesString = sizes
        .map((s) => {
          if (typeof s === "string") {
            return s;
          } else {
            return s.join("x");
          }
        })
        .join(", ");
    }

    return (
      <div
        className="border-l px-1 text-xs"
        // title="Benchmark Performance Multiplier"
      >
        BPM:{" "}
        {bpm < 1 ? (
          <>
            <SimpleTooltip
              tooltipText={
                <>
                  Enable benchmark tfcd abuse:{" "}
                  <b>{enableAbuse ? "true" : "false"}</b>
                  <br></br>
                  Benchmark Abuse Sizes: <b>{abuseSizesString}</b>
                </>
              }
            >
              <span className="font-bold text-orange-600">
                <NumberFormat value={bpm} format="0,0%"></NumberFormat>
              </span>
            </SimpleTooltip>
          </>
        ) : (
          <span className="text-gray-600">
            <NumberFormat value={bpm} format="0,0%"></NumberFormat>
          </span>
        )}
      </div>
    );
  }
}

class RequestTypesSection extends React.PureComponent {
  render() {
    const { requestTypes } = this.props;
    if (!requestTypes || requestTypes.length === 0) {
      return "";
    }

    return (
      <div className="my-1 flex">
        {requestTypes.map((d) => {
          if (d === "") return "";
          return (
            <div
              key={d}
              className="rounded mr-2 bg-gray-300 px-2 text-xs text-gray-700"
              title={d}
            >
              {d === "Google Publisher Tag" ? "GPT" : d}
            </div>
          );
        })}
      </div>
    );
  }
}
class DemandTypesSection extends React.PureComponent {
  render() {
    const { demandTypes } = this.props;
    if (!demandTypes || demandTypes.length === 0) {
      return "";
    }

    return (
      <>
        {demandTypes.map((d) => {
          return (
            <div
              key={d}
              className="rounded mr-2 bg-gray-300 px-2 text-xs text-gray-700"
            >
              {d}
            </div>
          );
        })}
      </>
    );
  }
}
export default UnitDetailHeader;
